import React, { useImperativeHandle, useMemo } from 'react';
import { useFormik, FieldArray, FormikProvider } from 'formik';

// components
import FormSectionHeader from '../../../components/forms/FormSectionHeader/FormSectionHeader.component';
import InputField from '../../../components/forms/InputField/InputField.component';
import EmergencyContactInput from '../../../components/forms/lists/EmergencyContactInput/EmergencyContactInput.component';
import CertificationInput from '../../../components/forms/lists/CertificationInput/CertificationInput.component';
import ActionButton from '../../../components/buttons/ActionButton/ActionButton.component';
import SelectField from '../../../components/forms/SelectField/SelectField.component';
import Checkbox from '../../../components/forms/Checkbox/Checkbox.component';

import moment from 'moment';
import { Grid, Divider, Alert } from '@mui/material';

// Dropdown options - VALUES are the ENUM KEYS (what GraphQL expects)
const classificationOptions = [
  { value: "SUPERINTENDENT", label: "Superintendent" },
  { value: "FOREMAN", label: "Foreman" },
  { value: "YARD", label: "Yard" },
  { value: "CLERK", label: "Clerk" },
  { value: "JOURNEYMAN", label: "Journeyman" },
  { value: "A1", label: "A1" },
  { value: "A2", label: "A2" },
  { value: "A3", label: "A3" },
  { value: "A4", label: "A4" },
  { value: "A5", label: "A5" },
  { value: "A6", label: "A6" },
  { value: "A7", label: "A7" },
  { value: "A8", label: "A8" },
  { value: "ADMIN", label: "Admin" },
];

const stateOptions = [
  { label: "Arizona", value: "AZ" },
  { label: "California", value: "CA" },
  { label: "Nevada", value: "NV" },
];

const ensureMoment = (d) => (moment(d).isValid() ? moment(d) : moment());
const s = (v) => (v == null ? '' : String(v));

/**
 * Converts any classification input to the ENUM KEY (token name)
 * Backend enum: A5 = "5", FOREMAN = "F", etc.
 * GraphQL expects: A5, FOREMAN (the keys, not the values)
 * This function handles:
 *   - Enum keys: "A5" -> "A5"
 *   - Enum values from DB: "5" -> "A5", "F" -> "FOREMAN"
 *   - EnumMeta strings: "<EnumMeta.A5: '5'>" -> "A5"
 */
const toEnumKey = (input) => {
  if (!input) return 'JOURNEYMAN';
  let raw = String(input).trim();

  // Handle EnumMeta format: "<EnumMeta.A5: '5'>" or "EnumMeta.FOREMAN"
  // FIX: Use \. (escaped dot) to match literal "." character
  if (raw.includes('EnumMeta') || raw.includes('<')) {
    const match = raw.match(/\.([A-Z0-9_]+)/i);
    if (match) raw = match[1];
  }

  raw = raw.toUpperCase();

  // Valid enum keys
  const validKeys = [
    'SUPERINTENDENT', 'FOREMAN', 'YARD', 'CLERK', 'JOURNEYMAN',
    'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'ADMIN'
  ];

  // If already a valid key, return it
  if (validKeys.includes(raw)) {
    return raw;
  }

  // Map enum VALUES to enum KEYS
  const valueToKey = {
    'S': 'SUPERINTENDENT',
    'F': 'FOREMAN',
    'Y': 'YARD',
    'C': 'CLERK',
    'J': 'JOURNEYMAN',
    '1': 'A1',
    '2': 'A2',
    '3': 'A3',
    '4': 'A4',
    '5': 'A5',
    '6': 'A6',
    '7': 'A7',
    '8': 'A8',
    'A': 'ADMIN',
  };

  if (valueToKey[raw]) {
    return valueToKey[raw];
  }

  console.warn(`Unknown classification: "${input}", defaulting to JOURNEYMAN`);
  return 'JOURNEYMAN';
};

const EditCrewMember = React.forwardRef(
  (
    {
      stage,
      prefillData = null,
      errors = {},
      foremanUsers = [],
      loadingForemanUsers = false,
    },
    ref
  ) => {
    const initialValues = useMemo(() => {
      const src = prefillData || {};

      const emergencyContacts = Array.isArray(src.emergencyContacts)
        ? src.emergencyContacts.map((c) => ({
            id: c?.id ?? null,
            firstName: s(c?.firstName),
            lastName: s(c?.lastName),
            phone: s(c?.phone),
            isPrimary: !!c?.isPrimary,
          }))
        : [];

      const certifications = Array.isArray(src.certifications)
        ? src.certifications.map((c) => ({
            id: c?.id ?? null,
            name: s(c?.name),
            expirationDate: ensureMoment(c?.expirationDate),
          }))
        : [];

      // Handle userId from prefillData.user or prefillData.userId
      let userIdValue = '';
      if (src.user?.id) {
        userIdValue = String(src.user.id);
      } else if (src.userId) {
        userIdValue = String(src.userId);
      }

      // Convert classification to enum KEY
      const classificationKey = toEnumKey(src.classification);
      console.log(
        'DEBUG: classification conversion:',
        src.classification,
        '->',
        classificationKey
      );

      return {
        firstName: s(src.firstName),
        lastName: s(src.lastName),
        phone: s(src.phone),
        email: s(src.email),
        classification: classificationKey, // Enum KEY like "A5", "FOREMAN"
        isForeman: !!src.isForeman,
        isActive: src.isActive !== false,
        street1: s(src.street1),
        street2: s(src.street2),
        city: s(src.city),
        state: s(src.state || 'CA'),
        zipcode: s(src.zipcode),
        userId: userIdValue,
        emergencyContacts,
        certifications,
        emergencyContactsToDelete: [],
        certificationsToDelete: [],
      };
    }, [prefillData]);

    const formik = useFormik({
      initialValues,
      enableReinitialize: true,
      onSubmit: () => {},
    });

    useImperativeHandle(ref, () => ({
      getData: () => {
        const formData = { ...formik.values };

        // Handle userId
        if (formData.userId === '' || formData.userId === null) {
          formData.userId = null;
        } else {
          formData.userId = parseInt(formData.userId, 10);
          if (isNaN(formData.userId)) formData.userId = null;
        }

        // Ensure classification is enum KEY
        formData.classification = toEnumKey(formData.classification);

        console.log('DEBUG: getData() classification:', formData.classification);

        return formData;
      },
    }));

    const stage0 = (
      <>
        <FormSectionHeader text="Requirements" />
        <p>
          Please make sure you complete the following before adding a new Crew
          Member:
        </p>
        <ul>
          <li>Application</li>
          <li>Union status</li>
          <li>Classification</li>
          <li>Verification & Copy of ID</li>
        </ul>

        <FormSectionHeader text="Name" />
        <InputField
          id="firstName"
          placeholder="First"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.firstName || ''}
          isValid={errors?.firstName}
        />
        <InputField
          id="lastName"
          placeholder="Last"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.lastName || ''}
          isValid={errors?.lastName}
        />

        <FormSectionHeader text="Contact" />
        <InputField
          id="phone"
          placeholder="Phone"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.phone || ''}
          required={false}
          isValid={errors?.phone}
        />
        <InputField
          id="email"
          placeholder="Email"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.email || ''}
          required={false}
          isValid={errors?.email}
        />

        <FormSectionHeader text="Assign to User Account" />
        <SelectField
          id={'userId'}
          label={'Select Foreman User'}
          options={[
            { label: 'None', value: '' },
            ...(foremanUsers || []).map((user) => ({
              value: String(user.id),
              label: `${user.firstName || ''} ${user.lastName || ''} (${
                user.username || 'No username'
              })`.trim(),
            })),
          ]}
          handleChange={(opt) =>
            formik.setFieldValue('userId', opt?.value ?? '')
          }
          value={formik.values.userId}
          required={false}
          isValid={errors?.userId}
          disabled={loadingForemanUsers}
        />
        {loadingForemanUsers && (
          <p
            style={{
              color: '#666',
              fontSize: '14px',
              marginTop: '-10px',
              marginBottom: '20px',
            }}
          >
            Loading foreman users...
          </p>
        )}

        <FormSectionHeader text="Address" />
        <InputField
          id="street1"
          placeholder="Street"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.street1 || ''}
          required={false}
        />
        <InputField
          id="street2"
          placeholder="Suite/Apt #"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.street2 || ''}
          required={false}
        />
        <InputField
          id="city"
          placeholder="City"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.city || ''}
          required={false}
        />
        <SelectField
          id="state"
          label="State"
          options={stateOptions}
          handleChange={(opt) =>
            formik.setFieldValue('state', opt?.value ?? '')
          }
          value={formik.values.state || ''}
          required={false}
        />
        <InputField
          id="zipcode"
          placeholder="Zipcode"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.zipcode || ''}
          required={false}
        />
      </>
    );

    const stage1 = (
      <>
        <SelectField
          id="classification"
          label="Classification"
          options={classificationOptions}
          handleChange={(opt) => {
            const key = opt?.value ?? 'JOURNEYMAN';
            console.log('DEBUG: Classification selected:', key);
            formik.setFieldValue('classification', key);
          }}
          value={formik.values.classification || 'JOURNEYMAN'}
          isValid={errors?.classification}
        />

        <Checkbox
          label="Is a Foreman"
          id="isForeman"
          isChecked={!!formik.values.isForeman}
          handleChange={formik.handleChange}
        />

        <Checkbox
          label="Is Active"
          id="isActive"
          isChecked={!!formik.values.isActive}
          handleChange={formik.handleChange}
        />

        <Divider sx={{ mt: 2 }} />

        <FieldArray
          name="emergencyContacts"
          render={({ remove, push }) => (
            <div>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <FormSectionHeader text="Emergency Contacts" />
                </Grid>
                <Grid item xs={6} textAlign="right" sx={{ mt: 2 }}>
                  <ActionButton
                    label="Add"
                    color="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      push({
                        id: null,
                        firstName: '',
                        lastName: '',
                        phone: '',
                        isPrimary: false,
                      });
                    }}
                  />
                </Grid>
              </Grid>

              {formik.values.emergencyContacts.map((contact, index) => (
                <EmergencyContactInput
                  key={contact.id ?? `ec-${index}`}
                  prefix={`emergencyContacts.${index}`}
                  handleChange={formik.handleChange}
                  firstName={contact.firstName || ''}
                  lastName={contact.lastName || ''}
                  phone={contact.phone || ''}
                  isPrimary={!!contact.isPrimary}
                  handleRemove={(e) => {
                    e.preventDefault();
                    const toDelete = [
                      ...(formik.values.emergencyContactsToDelete || []),
                    ];
                    if (contact.id) toDelete.push(contact.id);
                    formik.setFieldValue(
                      'emergencyContactsToDelete',
                      toDelete
                    );
                    remove(index);
                  }}
                  errors={errors?.emergencyContacts?.[index] || {}}
                />
              ))}
            </div>
          )}
        />

        <Divider sx={{ mt: 2 }} />

        <FieldArray
          name="certifications"
          render={({ remove, push }) => (
            <div>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <FormSectionHeader text="Certifications" />
                </Grid>
                <Grid item xs={6} textAlign="right" sx={{ mt: 2 }}>
                  <ActionButton
                    label="Add"
                    color="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      push({ id: null, name: '', expirationDate: moment() });
                    }}
                  />
                </Grid>
              </Grid>

              {formik.values.certifications.map((certification, index) => (
                <CertificationInput
                  key={certification.id ?? `cert-${index}`}
                  prefix={`certifications.${index}`}
                  handleChange={formik.handleChange}
                  handleDateChange={(value) =>
                    formik.setFieldValue(
                      `certifications.${index}.expirationDate`,
                      value
                    )
                  }
                  name={certification.name || ''}
                  expirationDate={ensureMoment(certification.expirationDate)}
                  handleRemove={(e) => {
                    e.preventDefault();
                    const toDelete = [
                      ...(formik.values.certificationsToDelete || []),
                    ];
                    if (certification.id) toDelete.push(certification.id);
                    formik.setFieldValue(
                      'certificationsToDelete',
                      toDelete
                    );
                    remove(index);
                  }}
                  errors={errors?.certifications?.[index] || {}}
                />
              ))}
            </div>
          )}
        />

        {errors?.__server && (
          <Alert sx={{ mt: 2 }} severity="error" variant="filled">
            {errors.__server}
          </Alert>
        )}
      </>
    );

    return (
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          {stage === 0 && stage0}
          {stage === 1 && stage1}
        </form>
      </FormikProvider>
    );
  }
);

export default EditCrewMember;
